import {
    useGetAccountRegistrationStatusQuery,
    useGetStudentDebtStatusQuery,
} from 'src/api/endpoints/students/personal/api';
import { useGetBranchesQuery } from 'src/api/endpoints/branches/api';
import { Role, UserRole } from 'src/api/endpoints/users/users.types.new';
import { useWhoamiQuery } from 'src/api/endpoints/auth/api';
import useAppDispatch from './hooks/useAppDispatch';
import { skipToken } from '@reduxjs/toolkit/query';
import { actions } from './store/user/user.slice';
import { ReactElement, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import { Routing, StudentInformation } from './components/Routing';
import 'react-toastify/dist/ReactToastify.css';
import { baseApi } from './api';
import {
    connect,
    connectToMessages,
    connectToNotifications,
    connectToPractices,
    disconnect,
    Topic,
} from './utils/ws/ws';
import { ConfigProvider } from 'antd';

import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import updateLocale from 'dayjs/plugin/updateLocale';
import localeData from 'dayjs/plugin/localeData';
import locale from 'antd/locale/ru_RU';
import { usePrimeReactLocale } from 'src/utils/prime-react/set-locale';

dayjs.extend(localeData);
dayjs.extend(updateLocale);
dayjs.updateLocale('ru', {
    weekStart: 1,
});

const {
    addNotification,
    removeNotification,
    addMessage,
    removeMessage,
    addPractice,
    removePractice,
} = actions;

export function App(): ReactElement {
    usePrimeReactLocale();

    const dispatch = useAppDispatch();

    const { data: whoami, isLoading: isWhoamiLoading } = useWhoamiQuery();
    const {
        data: organizationBranches,
        isLoading: isOrganizationBranchesLoading,
    } = useGetBranchesQuery(whoami ? undefined : skipToken);

    useEffect(() => {
        if (!whoami) return;

        const role: Role = whoami.role;

        const topics: Array<Topic> = [];

        topics.push(
            ...connectToNotifications(
                whoami.id,
                notification => dispatch(addNotification(notification)),
                notifications => dispatch(removeNotification(notifications))
            )
        );

        if (role.id === 1) {
            topics.push(
                ...connectToPractices(
                    whoami.id,
                    () => {
                        dispatch(addPractice());
                        dispatch(baseApi.util.invalidateTags(['Practice']));
                    },
                    () => {
                        dispatch(removePractice());
                        dispatch(baseApi.util.invalidateTags(['Practice']));
                    }
                )
            );
        }

        if (role.id !== 2 && role.id !== 1)
            topics.push(
                ...connectToMessages(
                    whoami.id,
                    () => {
                        dispatch(addMessage());
                        dispatch(baseApi.util.invalidateTags(['ChatMessage']));
                    },
                    () => {
                        dispatch(removeMessage());
                        dispatch(baseApi.util.invalidateTags(['ChatMessage']));
                    }
                )
            );

        connect(topics);
        return (): void => disconnect();
    }, [whoami]);

    const isStudent: boolean = whoami?.role?.id === UserRole.BranchStudent;

    const {
        data: isStudentAccountRegistered,
        isLoading: isStudentRegisteredStatusLoading,
    } = useGetAccountRegistrationStatusQuery(undefined, {
        skip: !isStudent,
    });

    const {
        data: isStudentHasDebts,
        isLoading: isStudentHasDebtsStatusLoading,
    } = useGetStudentDebtStatusQuery(undefined, {
        skip: !isStudent,
    });

    return (
        <ConfigProvider locale={locale}>
            <ToastContainer />
            <Routing
                user={isWhoamiLoading ? undefined : (whoami ?? null)}
                branches={
                    isOrganizationBranchesLoading
                        ? undefined
                        : (organizationBranches ?? [])
                }
                student={
                    isStudent
                        ? ({
                              loading:
                                  isStudentRegisteredStatusLoading ||
                                  isStudentHasDebtsStatusLoading,
                              hasDebts: isStudentHasDebts,
                              registered: isStudentAccountRegistered,
                          } as StudentInformation)
                        : null
                }
            />
        </ConfigProvider>
    );
}
